import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Storage } from '../storage';
import { TIMELINE_CATEGORIES, TIMELINE_CATEGORY_GROUPS } from '../constants';
import Icon from '../components/Icon';
import FeedPost from '../components/FeedPost';
import NotificationBell from '../components/NotificationBell';
import MobileMenu from '../components/MobileMenu';
import DevBlogFeed from '../components/DevBlogFeed';

// ─── FEED PAGE ────────────────────────────────────────────────────────────────
//
//  Paginated infinite-scroll social feed.
//  All filtering (category, keyword, following) is handled server-side via a
//  single unified Storage.getFeed() call so results are always consistent.
//
//  Infinite scroll uses IntersectionObserver on a sentinel div at the bottom of
//  the list.  Every filter/search/tab change resets to page 1 and clears the
//  accumulated posts array before fetching fresh results.
//
//  Props:
//    user           {object}   the signed-in user
//    onBack         {function} navigate back to dashboard
//    onViewTimeline {function} (tl, author) => void
//    theme          {string}
//    onThemeToggle  {function}
//

const FEED_LIMIT = 10;

const TIMELINE_ORIENTATIONS = [
  { value: 'horizontal', label: 'Horizontal', emoji: '↔️' },
  { value: 'vertical',   label: 'Vertical',   emoji: '↕️' },
  { value: 'zigzag',     label: 'Zigzag',     emoji: '⚡' },
  { value: 'spine',      label: 'Spine',      emoji: '🦴' },
  { value: 'newspaper',  label: 'Newspaper',  emoji: '📰' },
  { value: 'filmstrip',  label: 'Filmstrip',  emoji: '🎞️' },
  { value: 'storyline',  label: 'Storyline',  emoji: '📖' },
  { value: 'swimlane',   label: 'Swimlane',   emoji: '🏊' },
  { value: 'tree',       label: 'Tree',       emoji: '🌳' },
  { value: 'map',        label: 'Map',        emoji: '🗺️' },
];

// Tab values: 'all' (For You) | 'trending' | 'following'
// Persisted in localStorage so users land on their preferred tab.
const STORAGE_TAB_KEY = 'chronos_feed_tab';
const VALID_TABS = new Set(['all', 'trending', 'following', 'devblog']);
const readStoredTab = () => {
  const v = localStorage.getItem(STORAGE_TAB_KEY);
  return VALID_TABS.has(v) ? v : 'all';
};

const Feed = ({ user, onBack, onViewTimeline, onNotificationClick, onViewProfile, theme, onThemeToggle, onAuthRequired, guestMode, onGuestSignIn, initialDevBlogPostId, onDevBlogMounted }) => {

  // ── Feed state ──────────────────────────────────────────────────────────────
  const [posts,         setPosts]         = useState([]);
  const [social,        setSocial]        = useState({});
  const [followingIds,  setFollowingIds]  = useState(new Set());
  const [blockedIds,    setBlockedIds]    = useState(new Set());
  const [injectedRecs,  setInjectedRecs]  = useState([]); // recommendation cards injected every 10 posts
  const recsSourceRef = useRef(null);      // timeline ID used for the current batch of injected recs

  // ── Tab / filter / search state ─────────────────────────────────────────────
  // activeTab drives both the sort and the following-scope filter.
  //   'all'       → sort=recent, no following filter
  //   'trending'  → sort=trending, no following filter
  //   'following' → sort=recent, filter=following
  const [activeTab,         setActiveTab]         = useState(() => initialDevBlogPostId ? 'devblog' : readStoredTab());
  const [categoryFilter,    setCategoryFilter]    = useState([]);
  const [catDropOpen,       setCatDropOpen]       = useState(false);
  const catDropRef = useRef(null);
  const [orientationFilter, setOrientationFilter] = useState([]);
  const [typeDropOpen,      setTypeDropOpen]      = useState(false);
  const typeDropRef = useRef(null);
  const [searchQuery,       setSearchQuery]       = useState('');
  const [debouncedSearch,   setDebouncedSearch]   = useState('');
  const [searchScope,       setSearchScope]       = useState('timelines'); // 'timelines' | 'content'
  const [peopleResults,     setPeopleResults]     = useState([]);

  // Keep feedFilter in sync for code paths that still reference it
  const feedFilter = activeTab === 'following' ? 'following' : 'all';

  // ── Pagination state ────────────────────────────────────────────────────────
  const [page,        setPage]        = useState(1);
  const [hasMore,     setHasMore]     = useState(true);
  const [loading,     setLoading]     = useState(true);   // initial / reset load
  const [loadingMore, setLoadingMore] = useState(false);  // append load

  // ── Mobile nav ─────────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Refs ────────────────────────────────────────────────────────────────────
  const fetchIdRef   = useRef(0);      // stale-fetch guard
  const loadMoreRef  = useRef(null);   // stable ref so Observer closure stays fresh
  const observerRef  = useRef(null);   // holds the active IntersectionObserver

  // ── Social data — fetched per page alongside posts, merged into state ─────────
  // getSocial now requires explicit timeline IDs so the server never does a
  // full-table scan. fetchSocial is called after each page load with that page's IDs.
  const fetchSocial = useCallback((newPosts) => {
    const ids = newPosts.map(p => p.tl.id).filter(Boolean);
    if (!ids.length) return;
    Storage.getSocial(ids)
      .then(data => setSocial(prev => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  // ── Following IDs — loaded once (skip for guests) ───────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    Storage.getFollowing(user.id)
      .then(({ following }) => setFollowingIds(new Set(following.map(u => u.id))))
      .catch(() => {});
  }, [user?.id]);

  // ── Blocked IDs — loaded once (skip for guests) ─────────────────────────────
  useEffect(() => {
    if (!user) return;
    Storage.getBlockedUsers({ page: 1, limit: 200 })
      .then(users => setBlockedIds(new Set(users.map(u => u.id))))
      .catch(() => {});
  }, [!!user]);

  // ── Recommendations — fetch when first page loads ────────────────────────────
  // Picks the most-liked post as the seed timeline, fetches related timelines,
  // and shows them as a horizontal scroll section above the post list.
  useEffect(() => {
    if (!user || posts.length === 0 || loading) return;
    const topPost = [...posts].sort((a, b) => (b.tl.likeCount ?? 0) - (a.tl.likeCount ?? 0))[0];
    const sourceId = topPost?.tl?.id;
    if (!sourceId || recsSourceRef.current === sourceId) return;
    recsSourceRef.current = sourceId;
    Storage.getRecommendations(sourceId, 8)
      .then(({ recommendations }) => setInjectedRecs(recommendations || []))
      .catch(() => {});
  }, [user, loading, posts.length]);

  // ── Debounce search input (300 ms) ───────────────────────────────────────────
  // Clearing the search is immediate so the feed snaps back without waiting.
  useEffect(() => {
    if (!searchQuery.trim()) {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── People search — runs alongside timeline search for logged-in users ────────
  useEffect(() => {
    if (!user || !debouncedSearch) { setPeopleResults([]); return; }
    Storage.searchUsers(debouncedSearch)
      .then(users => setPeopleResults(users || []))
      .catch(() => setPeopleResults([]));
  }, [debouncedSearch, user]);

  // ── Persist tab selection ───────────────────────────────────────────────────
  const handleTabChange = (tab) => {
    if (tab === 'following' && !user) { onAuthRequired?.('follow'); return; }
    setActiveTab(tab);
    localStorage.setItem(STORAGE_TAB_KEY, tab);
    window.history.pushState({}, '', tab === 'devblog' ? '/dev-blog' : '/');
  };

  // ── Reset + fetch page 1 whenever any filter changes ────────────────────────
  // Depends on debouncedSearch (not searchQuery) so the request fires once,
  // after the user pauses typing, not on every keystroke.
  useEffect(() => {
    const fetchId = ++fetchIdRef.current;

    setPosts([]);
    setPage(1);
    setHasMore(true);
    setLoading(true);

    Storage.getFeed({
      page:        1,
      limit:       FEED_LIMIT,
      filter:      feedFilter,
      sort:        activeTab === 'trending' ? 'trending' : 'recent',
      category:    categoryFilter.length    ? categoryFilter.join(',')    : '',
      orientation: orientationFilter.length ? orientationFilter.join(',') : '',
      keyword:     debouncedSearch,
      scope:       searchScope,
    }).then(({ posts: newPosts, hasMore: more }) => {
      if (fetchIdRef.current !== fetchId) return;
      setPosts(newPosts);
      setHasMore(more);
      setLoading(false);
      fetchSocial(newPosts);
    }).catch(err => {
      if (fetchIdRef.current !== fetchId) return;
      setLoading(false);
    });
  }, [activeTab, categoryFilter, orientationFilter, debouncedSearch, searchScope]);

  // ── Load next page (appends to posts) ───────────────────────────────────────
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || loading) return;

    const nextPage = page + 1;
    const fetchId  = ++fetchIdRef.current;
    setLoadingMore(true);

    Storage.getFeed({
      page:        nextPage,
      limit:       FEED_LIMIT,
      filter:      feedFilter,
      sort:        activeTab === 'trending' ? 'trending' : 'recent',
      category:    categoryFilter.length    ? categoryFilter.join(',')    : '',
      orientation: orientationFilter.length ? orientationFilter.join(',') : '',
      keyword:     debouncedSearch,
      scope:       searchScope,
    }).then(({ posts: newPosts, hasMore: more }) => {
      if (fetchIdRef.current !== fetchId) return;
      setPosts(prev => [...prev, ...newPosts]);
      setPage(nextPage);
      setHasMore(more);
      setLoadingMore(false);
      fetchSocial(newPosts);
    }).catch(err => {
      if (fetchIdRef.current !== fetchId) return;
      setLoadingMore(false);
    });
  }, [loadingMore, hasMore, loading, page, activeTab, categoryFilter, orientationFilter, debouncedSearch, searchScope, fetchSocial]);

  // Keep the ref in sync so the Observer closure always calls the latest version.
  loadMoreRef.current = loadMore;

  // Callback ref: attaches the observer as soon as the sentinel mounts (or re-mounts).
  // This avoids the [] effect firing before the sentinel exists in the DOM.
  const sentinelRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) loadMoreRef.current(); },
        { rootMargin: '300px' }
      );
      observer.observe(node);
      observerRef.current = observer;
    }
  }, []); // stable — loadMoreRef indirection keeps the closure fresh

  // ── Social + follow actions ──────────────────────────────────────────────────
  const handleSocialUpdate = async (timelineId, newData) => {
    if (!user) { onAuthRequired?.('like'); return; }
    setSocial(prev => ({ ...prev, [timelineId]: newData }));
    await Storage.updateSocial(timelineId, newData);
  };

  const handleBlock = useCallback((authorId) => {
    if (!user) { onAuthRequired?.('follow'); return; }
    setBlockedIds(prev => new Set([...prev, authorId]));
    setPosts(prev => prev.filter(({ author }) => author.id !== authorId));
  }, [user, onAuthRequired]);

  const handleFollowToggle = async (authorId) => {
    if (!user) { onAuthRequired?.('follow'); return; }
    const wasFollowing = followingIds.has(authorId);
    setFollowingIds(prev => {
      const next = new Set(prev);
      wasFollowing ? next.delete(authorId) : next.add(authorId);
      return next;
    });
    try {
      await (wasFollowing ? Storage.unfollow(authorId) : Storage.follow(authorId));
    } catch {
      setFollowingIds(prev => {
        const next = new Set(prev);
        wasFollowing ? next.add(authorId) : next.delete(authorId);
        return next;
      });
    }
  };

  // ── Search input handler ─────────────────────────────────────────────────────
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    // Clearing search bypasses the debounce for immediate feedback.
    if (!value.trim()) setDebouncedSearch('');
  };

  // ── Close category dropdown on outside click ────────────────────────────────
  useEffect(() => {
    if (!catDropOpen) return;
    const handler = (e) => {
      if (catDropRef.current && !catDropRef.current.contains(e.target)) setCatDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [catDropOpen]);

  // ── Close type dropdown on outside click ─────────────────────────────────────
  useEffect(() => {
    if (!typeDropOpen) return;
    const handler = (e) => {
      if (typeDropRef.current && !typeDropRef.current.contains(e.target)) setTypeDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [typeDropOpen]);

  // ── Derived state ─────────────────────────────────────────────────────────────
  const isSearchPending    = searchQuery.trim() !== debouncedSearch;
  const isTrending         = activeTab === 'trending';
  const showEmptyFollowing = !loading && posts.length === 0 && activeTab === 'following' && !debouncedSearch;
  const showEmptyTrending  = !loading && posts.length === 0 && isTrending && !debouncedSearch && !categoryFilter.length && !orientationFilter.length;
  const showNoResults      = !loading && posts.length === 0 && !!debouncedSearch;
  const showNoFilter       = !loading && posts.length === 0 && (categoryFilter.length > 0 || orientationFilter.length > 0) && !debouncedSearch;
  const showEmptyFeed      = !loading && posts.length === 0 && !showEmptyFollowing && !showNoResults && !showNoFilter && !showEmptyTrending;

  // ── Category dropdown label ───────────────────────────────────────────────────
  const catLabel = categoryFilter.length === 0
    ? 'All Categories'
    : categoryFilter.length === 1
      ? (() => { const c = TIMELINE_CATEGORIES.find(x => x.value === categoryFilter[0]); return c ? `${c.emoji} ${c.label}` : '1 selected'; })()
      : `${categoryFilter.length} selected`;

  // ── Type dropdown label ───────────────────────────────────────────────────────
  const typeLabel = orientationFilter.length === 0
    ? 'All Types'
    : orientationFilter.length === 1
      ? (() => { const o = TIMELINE_ORIENTATIONS.find(x => x.value === orientationFilter[0]); return o ? `${o.emoji} ${o.label}` : '1 selected'; })()
      : `${orientationFilter.length} selected`;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ── Nav ── */}
      <nav style={{
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        padding: '0 16px', display: 'flex', alignItems: 'center', gap: 12, height: 64,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <button
          onClick={() => handleTabChange('all')}
          style={{ display: 'flex', alignItems: 'center', gap: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', flex: 1, minWidth: 0 }}
          aria-label="Go to feed home"
        >
          <div style={{ fontSize: 22, marginRight: 4, filter: 'brightness(0) invert(1)' }} aria-hidden="true">⏱</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--accent)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: 0 }}>
            Chronos <span style={{ fontSize: 14, color: 'var(--text3)', fontFamily: 'var(--font-body)', fontWeight: 400 }}>/ Feed</span>
          </h1>
        </button>

        {/* Desktop nav items */}
        {!isMobile && (<>
          <button
            className="btn-ghost btn-sm"
            onClick={onThemeToggle}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, padding: 0 }}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
          </button>
          {user && <NotificationBell userId={user.id} onViewTimeline={onNotificationClick} onViewProfile={onViewProfile} />}
          {onBack && (
            <button className="btn-ghost btn-sm" onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="back" size={14} /> My Dashboard
            </button>
          )}
        </>)}

        {/* Mobile hamburger */}
        {isMobile && (
          <MobileMenu>
            {(close) => <>
              {onBack && (
                <button
                  onClick={() => { onBack(); close(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
                >
                  <Icon name="back" size={15} /> My Dashboard
                </button>
              )}
              {user && (
                <div style={{ padding: '4px 16px' }}>
                  <NotificationBell userId={user.id} onViewTimeline={onNotificationClick} onViewProfile={onViewProfile} />
                </div>
              )}
              <button
                onClick={() => { onThemeToggle(); close(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', background: 'none', border: 'none', fontSize: 14, color: 'var(--text)', cursor: 'pointer', textAlign: 'left' }}
              >
                <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </button>
            </>}
          </MobileMenu>
        )}
      </nav>

      {/* ── Tab row: For You / Trending / Following ── */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'center', padding: '0 24px' }}>
        <div style={{ display: 'flex', gap: 0 }}>
          {[
            ['all',       'For You'],
            ['trending',  'Trending'],
            ['following', 'Following'],
            ['devblog',   'Dev Blog'],
          ].map(([val, label]) => (
            <button
              key={val}
              onClick={() => handleTabChange(val)}
              style={{
                background: 'none', border: 'none', padding: '12px 20px',
                fontSize: 14, fontWeight: activeTab === val ? 600 : 400,
                color: activeTab === val ? 'var(--accent)' : 'var(--text2)',
                cursor: 'pointer', position: 'relative',
                borderBottom: activeTab === val ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: -2, transition: 'color 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Search + category filter bar ── */}
      {activeTab !== 'devblog' && <div className="feed-filter-bar" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: isMobile ? '10px 12px' : '12px 24px' }}>

        {/* Search input */}
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <div style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--text3)', pointerEvents: 'none', display: 'flex',
          }}>
            <Icon name="search" size={15} />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder="Search by title, description, category, or author…"
            aria-label="Search the feed"
            style={{ paddingLeft: 36, paddingRight: searchQuery ? 36 : 14 }}
          />
          {searchQuery && (
            <button
              onClick={() => handleSearchChange('')}
              aria-label="Clear search"
              style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: 'var(--text3)',
                cursor: 'pointer', padding: 4, display: 'flex',
              }}
            >
              <Icon name="close" size={14} />
            </button>
          )}
        </div>

        {/* Scope selector — shown while search is active */}
        {searchQuery.trim() && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>Search in:</span>
            {[['timelines', 'Timelines'], ['content', 'All Content']].map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setSearchScope(val)}
                style={{
                  fontSize: 12, padding: '4px 10px', borderRadius: 'var(--radius)',
                  border: `1px solid ${searchScope === val ? 'var(--accent)' : 'var(--border2)'}`,
                  background: searchScope === val ? 'var(--accent)' : 'transparent',
                  color: searchScope === val ? 'var(--on-accent)' : 'var(--text2)',
                  cursor: 'pointer', fontWeight: searchScope === val ? 600 : 400,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Filter dropdowns row */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>

          {/* Category multiselect dropdown */}
          <div ref={catDropRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={() => setCatDropOpen(o => !o)}
              aria-haspopup="listbox"
              aria-expanded={catDropOpen}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 12px', borderRadius: 'var(--radius)',
                border: `1px solid ${categoryFilter.length > 0 ? 'var(--accent)' : 'var(--border2)'}`,
                background: categoryFilter.length > 0 ? 'var(--accent)10' : 'var(--surface)',
                color: categoryFilter.length > 0 ? 'var(--accent)' : 'var(--text2)',
                fontSize: 13, fontWeight: categoryFilter.length > 0 ? 600 : 400,
                cursor: 'pointer', whiteSpace: 'nowrap', minWidth: isMobile ? 0 : 150,
                transition: 'border-color 0.15s, background 0.15s, color 0.15s',
              }}
            >
              <Icon name="feed" size={13} />
              <span style={{ flex: 1, textAlign: 'left' }}>{catLabel}</span>
              {categoryFilter.length > 0 && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear category filter"
                  onClick={e => { e.stopPropagation(); setCategoryFilter([]); }}
                  onKeyDown={e => e.key === 'Enter' && (e.stopPropagation(), setCategoryFilter([]))}
                  style={{ display: 'flex', alignItems: 'center', color: 'var(--accent)', opacity: 0.7 }}
                >
                  <Icon name="close" size={12} />
                </span>
              )}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ flexShrink: 0, transform: catDropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                <path d="M2 3.5L5 6.5L8 3.5" />
              </svg>
            </button>

            {catDropOpen && (
              <div
                role="listbox"
                aria-multiselectable="true"
                style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200,
                  background: 'var(--surface)', border: '1px solid var(--border2)',
                  borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  minWidth: 240, maxWidth: 'calc(100vw - 24px)', maxHeight: 320, overflowY: 'auto',
                  padding: '6px 0',
                }}
              >
                {TIMELINE_CATEGORY_GROUPS.map(group => (
                  <div key={group.label}>
                    <p style={{
                      margin: 0, padding: '8px 14px 4px',
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.08em', color: 'var(--text3)',
                      borderTop: '1px solid var(--border)',
                    }}>
                      {group.label}
                    </p>
                    {group.values.map(val => {
                      const cat = TIMELINE_CATEGORIES.find(c => c.value === val);
                      if (!cat) return null;
                      const checked = categoryFilter.includes(cat.value);
                      return (
                        <label
                          key={cat.value}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '7px 14px', cursor: 'pointer', fontSize: 13,
                            color: checked ? 'var(--accent)' : 'var(--text)',
                            background: checked ? 'var(--accent)0d' : 'transparent',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => { if (!checked) e.currentTarget.style.background = 'var(--hover)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = checked ? 'var(--accent)0d' : 'transparent'; }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setCategoryFilter(prev =>
                                checked ? prev.filter(v => v !== cat.value) : [...prev, cat.value]
                              );
                            }}
                            style={{ accentColor: 'var(--accent)', width: 14, height: 14, flexShrink: 0 }}
                          />
                          <span aria-hidden="true" style={{ fontSize: 15 }}>{cat.emoji}</span>
                          <span>{cat.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Timeline type multiselect dropdown */}
          <div ref={typeDropRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={() => setTypeDropOpen(o => !o)}
              aria-haspopup="listbox"
              aria-expanded={typeDropOpen}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 12px', borderRadius: 'var(--radius)',
                border: `1px solid ${orientationFilter.length > 0 ? 'var(--accent)' : 'var(--border2)'}`,
                background: orientationFilter.length > 0 ? 'var(--accent)10' : 'var(--surface)',
                color: orientationFilter.length > 0 ? 'var(--accent)' : 'var(--text2)',
                fontSize: 13, fontWeight: orientationFilter.length > 0 ? 600 : 400,
                cursor: 'pointer', whiteSpace: 'nowrap', minWidth: isMobile ? 0 : 130,
                transition: 'border-color 0.15s, background 0.15s, color 0.15s',
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 13 }}>🗂️</span>
              <span style={{ flex: 1, textAlign: 'left' }}>{typeLabel}</span>
              {orientationFilter.length > 0 && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear type filter"
                  onClick={e => { e.stopPropagation(); setOrientationFilter([]); }}
                  onKeyDown={e => e.key === 'Enter' && (e.stopPropagation(), setOrientationFilter([]))}
                  style={{ display: 'flex', alignItems: 'center', color: 'var(--accent)', opacity: 0.7 }}
                >
                  <Icon name="close" size={12} />
                </span>
              )}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ flexShrink: 0, transform: typeDropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                <path d="M2 3.5L5 6.5L8 3.5" />
              </svg>
            </button>

            {typeDropOpen && (
              <div
                role="listbox"
                aria-multiselectable="true"
                style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200,
                  background: 'var(--surface)', border: '1px solid var(--border2)',
                  borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  minWidth: 200, maxWidth: 'calc(100vw - 24px)', maxHeight: 320, overflowY: 'auto',
                  padding: '6px 0',
                }}
              >
                {TIMELINE_ORIENTATIONS.map(orient => {
                  const checked = orientationFilter.includes(orient.value);
                  return (
                    <label
                      key={orient.value}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 14px', cursor: 'pointer', fontSize: 13,
                        color: checked ? 'var(--accent)' : 'var(--text)',
                        background: checked ? 'var(--accent)0d' : 'transparent',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => { if (!checked) e.currentTarget.style.background = 'var(--hover)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = checked ? 'var(--accent)0d' : 'transparent'; }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setOrientationFilter(prev =>
                            checked ? prev.filter(v => v !== orient.value) : [...prev, orient.value]
                          );
                        }}
                        style={{ accentColor: 'var(--accent)', width: 14, height: 14, flexShrink: 0 }}
                      />
                      <span aria-hidden="true" style={{ fontSize: 15 }}>{orient.emoji}</span>
                      <span>{orient.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>}

      {/* ── Dev Blog ── */}
      {activeTab === 'devblog' && (
        <main className="feed-main" style={{ maxWidth: 680, margin: '0 auto', padding: '28px 16px 60px' }}>
          {peopleResults.length > 0 && debouncedSearch && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                People
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {peopleResults.map(person => (
                  <button
                    key={person.id}
                    type="button"
                    onClick={() => onViewProfile?.(person.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-xl)', padding: '10px 14px',
                      cursor: 'pointer', textAlign: 'left', width: '100%',
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    {person.avatar
                      ? <img src={person.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--accent)', color: 'var(--on-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                          {(person.name || '?').slice(0, 1).toUpperCase()}
                        </div>
                    }
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{person.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--accent)', marginLeft: 'auto', flexShrink: 0 }}>View Profile</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <DevBlogFeed
            initialPostId={typeof initialDevBlogPostId === 'string' ? initialDevBlogPostId : null}
            onMounted={onDevBlogMounted}
          />
        </main>
      )}

      {/* ── Post list ── */}
      {activeTab !== 'devblog' && (
      <main className="feed-main" style={{ maxWidth: 680, margin: '0 auto', padding: '28px 16px 60px' }}>

        {/* Initial loading — skeleton cards while searching, simple text otherwise */}
        {(loading || isSearchPending) && posts.length === 0 ? (
          debouncedSearch || isSearchPending ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-xl)', overflow: 'hidden', animation: 'pulse 1.6s ease-in-out infinite',
                  animationDelay: `${i * 0.15}s`,
                }}>
                  <div style={{ height: 120, background: 'var(--bg2)' }} />
                  <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ height: 16, width: '60%', background: 'var(--bg2)', borderRadius: 4 }} />
                    <div style={{ height: 12, width: '40%', background: 'var(--bg2)', borderRadius: 4 }} />
                    <div style={{ height: 12, width: '80%', background: 'var(--bg2)', borderRadius: 4 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text3)' }}>
              <p style={{ fontSize: 14 }}>Loading feed…</p>
            </div>
          )

        ) : showEmptyFollowing ? (
          <div className="feed-empty-following">
            <div style={{ fontSize: 52, marginBottom: 16 }} aria-hidden="true">👥</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--text)', marginBottom: 10 }}>
              You're not following anyone yet
            </h2>
            <p style={{ fontSize: 14, color: 'var(--text2)', maxWidth: 380, margin: '0 auto 24px', lineHeight: 1.6 }}>
              Discover creators in the For You tab and follow them to build your feed.
            </p>
            <button className="btn-primary" onClick={() => handleTabChange('all')}>Switch to For You</button>
          </div>

        ) : showEmptyTrending ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text3)' }}>
            <div style={{ fontSize: 52, marginBottom: 16 }} aria-hidden="true">🔥</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--text)', marginBottom: 10 }}>
              Nothing is trending yet
            </h2>
            <p style={{ fontSize: 14, color: 'var(--text2)', maxWidth: 380, margin: '0 auto 24px', lineHeight: 1.6 }}>
              Be the first to create something worth talking about!
            </p>
            <button className="btn-primary" onClick={onBack}>Create Timeline</button>
          </div>

        ) : showNoResults ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }} aria-hidden="true">🔍</div>
            <p style={{ fontSize: 15, marginBottom: 16 }}>
              No timelines match <strong style={{ color: 'var(--text2)' }}>"{debouncedSearch}"</strong>
            </p>
            <button className="btn-ghost btn-sm" onClick={() => handleSearchChange('')}>Clear search</button>
          </div>

        ) : showNoFilter ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
            <p style={{ fontSize: 15 }}>No public timelines match the selected filters.</p>
            <button
              className="btn-ghost btn-sm"
              onClick={() => { setCategoryFilter([]); setOrientationFilter([]); }}
              style={{ marginTop: 12 }}
            >
              Clear filters
            </button>
          </div>

        ) : showEmptyFeed ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text3)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }} aria-hidden="true">📡</div>
            <p style={{ fontSize: 16, marginBottom: 8 }}>Nothing in the feed yet</p>
            <p style={{ fontSize: 14 }}>Public timelines created by any user will appear here.</p>
          </div>

        ) : (
          <>
            {/* ── Recommendations strip ── */}
            {user && injectedRecs.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                  You might also like
                </p>
                <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 6, scrollbarWidth: 'thin' }}>
                  {injectedRecs.map(({ tl: rec, author: recAuthor }) => {
                    const accent = rec.colors?.primary || rec.colors?.accent || 'var(--accent)';
                    return (
                      <button
                        key={rec.id}
                        type="button"
                        onClick={() => onViewTimeline(rec, recAuthor)}
                        style={{
                          flexShrink: 0, width: 200, textAlign: 'left',
                          background: 'var(--surface)', border: `1px solid color-mix(in srgb, ${accent} 25%, var(--border))`,
                          borderRadius: 'var(--radius-xl)', overflow: 'hidden',
                          cursor: 'pointer', transition: 'border-color 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = accent; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = `color-mix(in srgb, ${accent} 25%, var(--border))`; }}
                        aria-label={`${rec.title} by ${recAuthor.name}`}
                      >
                        <div style={{ height: 5, background: accent }} />
                        <div style={{ padding: '10px 12px' }}>
                          <p style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', lineHeight: 1.3, marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {rec.title}
                          </p>
                          <p style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            {recAuthor.name}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* People results — shown when searching by display name */}
            {peopleResults.length > 0 && debouncedSearch && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                  People
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {peopleResults.map(person => (
                    <button
                      key={person.id}
                      type="button"
                      onClick={() => onViewProfile?.(person.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        background: 'var(--surface)', border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-xl)', padding: '10px 14px',
                        cursor: 'pointer', textAlign: 'left', width: '100%',
                        transition: 'border-color 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                    >
                      {person.avatar
                        ? <img src={person.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                        : <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--accent)', color: 'var(--on-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                            {(person.name || '?').slice(0, 1).toUpperCase()}
                          </div>
                      }
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{person.name}</span>
                      <span style={{ fontSize: 12, color: 'var(--accent)', marginLeft: 'auto', flexShrink: 0 }}>View Profile</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {posts.map(({ tl, author }) => {
                return (
                  <div key={tl.id}>
                    <FeedPost
                      tl={tl}
                      author={author}
                      social={social[tl.id] || {}}
                      currentUser={user}
                      onSocialUpdate={handleSocialUpdate}
                      onViewTimeline={onViewTimeline}
                      onViewProfile={onViewProfile}
                      isFollowing={followingIds.has(author.id)}
                      onFollowToggle={handleFollowToggle}
                      blockedIds={blockedIds}
                      onBlock={handleBlock}
                      isTrendingTab={isTrending}
                      trendingRank={isTrending ? (tl.trendingRank ?? null) : null}
                      trendingScore={isTrending ? (tl.trendingScore ?? 0) : 0}
                      likes7d={isTrending ? (tl.likes7d ?? 0) : 0}
                      comments7d={isTrending ? (tl.comments7d ?? 0) : 0}
                      views7d={isTrending ? (tl.views7d ?? 0) : 0}
                      matchedEvents={tl.matchedEvents || []}
                      searchQuery={debouncedSearch}
                      onAuthRequired={onAuthRequired}
                    />
                  </div>
                );
              })}
            </div>

            {/* Sentinel — IntersectionObserver fires loadMore when this enters the viewport */}
            <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />

            {/* Spinner while the next page is fetching */}
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text3)' }}>
                <p style={{ fontSize: 13 }}>Loading more…</p>
              </div>
            )}

            {/* End-of-feed message once all posts are loaded */}
            {!hasMore && !loadingMore && posts.length > 0 && (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text3)' }}>
                <div style={{ width: 40, height: 1, background: 'var(--border)', display: 'inline-block', verticalAlign: 'middle', marginRight: 10 }} aria-hidden="true" />
                <span style={{ fontSize: 13 }}>You've reached the end</span>
                <div style={{ width: 40, height: 1, background: 'var(--border)', display: 'inline-block', verticalAlign: 'middle', marginLeft: 10 }} aria-hidden="true" />
              </div>
            )}
          </>
        )}
      </main>
      )}

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '32px 24px 24px', borderTop: '1px solid var(--border)' }}>
        <p style={{ fontSize: 12, color: 'var(--text3)' }}>
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text3)', textDecoration: 'underline' }}>Terms of Service</a>
          {' · '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text3)', textDecoration: 'underline' }}>Privacy Policy</a>
        </p>
      </footer>
    </div>
  );
};

export default Feed;
